import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FaFilePdf, FaFileAlt, FaDownload, FaTrashAlt, FaSpinner } from 'react-icons/fa';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import api from '../../../../api';

const DocumentSection = ({
  title,
  documents = [],
  onUpload,
  onDelete,
  maxFiles = 10,
  readOnly = false
}) => {

  const [uploading, setUploading] = useState(false);
  const [deletingFileName, setDeletingFileName] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]);
  
  // Confirmation modal state
  const [deleteFileModal, setDeleteFileModal] = useState({ 
    show: false, 
    fileName: null 
  });


  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!readOnly) {
        setUploading(true);
        onUpload(acceptedFiles);
        setPreviewFiles(
          acceptedFiles.map(file => ({
            name: file.name,
            size: file.size,
            type: file.type
          }))
        );
        setUploading(false);
      }
    },
    [onUpload, readOnly]
  );

  const [cid1Files, setCid1Files] = useState([]); // atau documents
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles,
    disabled: readOnly
  });

  const getIconForFile = (fileName) => {
    const ext = fileName?.toLowerCase();
    if (ext.endsWith('.pdf')) return <FaFilePdf className="text-danger me-2 fs-5" />;
    return <FaFileAlt className="text-secondary me-2 fs-5" />;
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024,
      dm = decimals < 0 ? 0 : decimals,
      sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const handleDeleteFile = (fileName) => {
    // If onDelete prop is provided, let the parent handle the confirmation and deletion
    if (onDelete) {
      onDelete(fileName);
      return;
    }
    
    // Otherwise, show confirmation modal
    setDeleteFileModal({
      show: true,
      fileName: fileName
    });
  };

  const handleConfirmDeleteFile = async () => {
    const { fileName } = deleteFileModal;
    
    try {
      setDeletingFileName(fileName);
      await api.delete(`/lead-services/lead-docs/${fileName}`);
      // Refresh the documents list or handle success
    } catch (error) {
      console.error("Gagal menghapus file:", error);
      alert("Gagal menghapus file dari server.");
    } finally {
      setDeletingFileName(null);
      setDeleteFileModal({ show: false, fileName: null });
    }
  };

  return (
    <div className="document-section mb-4">
      <h5 className="fw-semibold mb-3">{title}</h5>

      {!readOnly && documents.length === 0 && (
        // Show upload area only when no documents exist
        <div
          {...getRootProps()}
          className={`card border-dashed shadow-sm mb-3 p-4 text-center ${isDragActive ? 'bg-light border-primary' : 'border-secondary'}`}
          style={{ cursor: 'pointer', borderStyle: 'dashed' }}
        >
          <input {...getInputProps()} />
          <p className="mb-0 text-muted small">
            {uploading ? <FaSpinner className="me-2 spinner-border-sm" /> : null}
            {isDragActive ? 'Lepaskan file di sini...' : 'Klik atau seret file ke sini untuk mengunggah'}
          </p>
          <p className="mb-0 text-muted small mt-2">No documents have been uploaded yet.</p>
        </div>
      )}

      {previewFiles.length > 0 && (
        <div className="alert alert-info small">
          File siap diupload:
          <ul className="mb-0">
            {previewFiles.map((file, idx) => (
              <li key={idx}>{file.name} ({formatBytes(file.size)})</li>
            ))}
          </ul>
        </div>
      )}

      {documents && documents.length > 0 ? (
        <ul className="list-group shadow-sm">
          {documents.map((doc, idx) => (
            <li
              key={idx}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <div className="d-flex align-items-center text-truncate" style={{ maxWidth: '85%' }}>
                {getIconForFile(doc.name)}
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none text-dark text-truncate"
                  title={doc.name}
                >
                  {doc.name}
                </a>
                <span className="badge bg-light text-muted ms-2">{doc.size ? formatBytes(doc.size) : ''}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-muted">
                  <FaDownload />
                </a>
                {!readOnly && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0"
                    onClick={() => handleDeleteFile(doc.name)}
                    disabled={deletingFileName === doc.name}
                  >
                    {deletingFileName === doc.name ? <FaSpinner className="spinner-border-sm" /> : <FaTrashAlt />}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : readOnly ? (
        // Only show "no documents" message in read-only mode when no documents exist
        <div className="text-center text-muted py-3 small">Belum ada dokumen yang diunggah</div>
      ) : null}
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteFileModal.show}
        onClose={() => setDeleteFileModal({ show: false, fileName: null })}
        onConfirm={handleConfirmDeleteFile}
        title="Delete File"
        message="Are you sure you want to delete this? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        modalId="delete-document-modal"
      />
    </div>
  );

};

export default DocumentSection;
